/**
 * Formatting primitives. Money is the important one.
 *
 * House rule (see AGENTS.md): money is integer cents everywhere, never floats.
 * Nothing in this app divides by 100 outside this file.
 */

/** Integer cents. Kept as a plain number but named so intent is visible. */
export type Cents = number;

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const usdCompact = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

/** `1234` → `$12.34`. Negative values render as `-$12.34`. */
export function money(cents: Cents | null | undefined, fallback = '—'): string {
  if (cents === null || cents === undefined || Number.isNaN(cents)) return fallback;
  return usd.format(cents / 100);
}

/** `$12.3k` — for KPI tiles and axis labels only, never for a ledger. */
export function moneyCompact(cents: Cents | null | undefined, fallback = '—'): string {
  if (cents === null || cents === undefined || Number.isNaN(cents)) return fallback;
  if (Math.abs(cents) < 100_000) return money(cents);
  return usdCompact.format(cents / 100);
}

/** Bare `12.34`, no symbol — for right-aligned numeric table columns where the
 *  header already carries the unit. */
export function moneyPlain(cents: Cents | null | undefined, fallback = '—'): string {
  if (cents === null || cents === undefined || Number.isNaN(cents)) return fallback;
  return (cents / 100).toFixed(2);
}

/**
 * Parse operator input into integer cents. Accepts `12`, `12.3`, `$12.34`,
 * `1,234.56`, `(5.00)` for negative. Returns null if it is not a number.
 * Rounds half-away-from-zero so `0.005` → 1¢, matching a cash drawer.
 */
export function parseMoney(input: string): Cents | null {
  const raw = input.trim();
  if (!raw) return null;
  const negative = /^\(.*\)$/.test(raw) || raw.startsWith('-');
  const cleaned = raw.replace(/[$,()\s-]/g, '');
  if (!/^\d*\.?\d*$/.test(cleaned) || cleaned === '' || cleaned === '.') return null;
  const value = Number.parseFloat(cleaned);
  if (Number.isNaN(value)) return null;
  const cents = Math.round(Math.abs(value) * 100 + Number.EPSILON);
  return negative ? -cents : cents;
}

const number = new Intl.NumberFormat('en-US');

export function num(value: number | null | undefined, fallback = '—'): string {
  if (value === null || value === undefined || Number.isNaN(value)) return fallback;
  return number.format(value);
}

/** Quantities may be fractional (weighed goods). Trim pointless zeros. */
export function qty(value: number | string | null | undefined, fallback = '—'): string {
  if (value === null || value === undefined) return fallback;
  const n = typeof value === 'string' ? Number.parseFloat(value) : value;
  if (Number.isNaN(n)) return fallback;
  if (Number.isInteger(n)) return number.format(n);
  return n.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
}

export function percent(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${value.toFixed(digits)}%`;
}

/** Margin from integer cents. Returns null when price is zero/unknown. */
export function marginPct(priceCents: Cents, costCents: Cents): number | null {
  if (!priceCents) return null;
  return ((priceCents - costCents) / priceCents) * 100;
}

/* ── Dates ────────────────────────────────────────────────────────────────
   The store runs in one timezone; everything here formats in the browser's
   local zone, which is the operator's zone.                                */

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const dtDate = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
const dtDateShort = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const dtTime = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
});
const dtFull = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

export function date(value: Parameters<typeof toDate>[0], fallback = '—'): string {
  const d = toDate(value);
  return d ? dtDate.format(d) : fallback;
}

/**
 * A calendar day the API sent as `YYYY-MM-DD` (a store day: `last_paid_on`, `arrived_on`), shown as that
 * day. Parsing it as a Date would read it as UTC midnight and show the evening before in the store's zone.
 */
export function dayLabel(value: string | null | undefined, fallback = '—'): string {
  if (!value) return fallback;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return date(value, fallback);
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.getFullYear() === new Date().getFullYear()
    ? dtDateShort.format(d)
    : dtDate.format(d);
}

export function dateTime(value: Parameters<typeof toDate>[0], fallback = '—'): string {
  const d = toDate(value);
  return d ? dtFull.format(d) : fallback;
}

export function time(value: Parameters<typeof toDate>[0], fallback = '—'): string {
  const d = toDate(value);
  return d ? dtTime.format(d) : fallback;
}

/**
 * `today 3:14 PM` / `yesterday 9:02 AM` / `Mar 4` — the form an operator
 * actually wants in a busy list: recent things get a time, old things a date.
 */
export function smartDate(value: Parameters<typeof toDate>[0], fallback = '—'): string {
  const d = toDate(value);
  if (!d) return fallback;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dayMs = 86_400_000;
  const delta = d.getTime() - startOfToday.getTime();
  if (delta >= 0 && delta < dayMs) return dtTime.format(d);
  if (delta >= -dayMs && delta < 0) return `Yst ${dtTime.format(d)}`;
  if (d.getFullYear() === now.getFullYear()) return dtDateShort.format(d);
  return dtDate.format(d);
}

/** `3m ago`, `2h ago`, `4d ago`. Used for freshness/heartbeat indicators. */
export function relativeTime(
  value: Parameters<typeof toDate>[0],
  fallback = '—'
): string {
  const d = toDate(value);
  if (!d) return fallback;
  const seconds = Math.round((Date.now() - d.getTime()) / 1000);
  const future = seconds < 0;
  const s = Math.abs(seconds);
  let out: string;
  if (s < 10) return 'just now';
  if (s < 60) out = `${s}s`;
  else if (s < 3600) out = `${Math.floor(s / 60)}m`;
  else if (s < 86_400) out = `${Math.floor(s / 3600)}h`;
  else if (s < 2_592_000) out = `${Math.floor(s / 86_400)}d`;
  else if (s < 31_536_000) out = `${Math.floor(s / 2_592_000)}mo`;
  else out = `${Math.floor(s / 31_536_000)}y`;
  return future ? `in ${out}` : `${out} ago`;
}

/** Whole days until a date; negative when already past. For expiry rails. */
export function daysUntil(value: Parameters<typeof toDate>[0]): number | null {
  const d = toDate(value);
  if (!d) return null;
  const today = new Date();
  const a = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const b = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((a - b) / 86_400_000);
}

/** `2026-08-14` for date inputs and API query params. */
export function isoDate(value: Parameters<typeof toDate>[0]): string {
  const d = toDate(value) ?? new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/* ── Text ─────────────────────────────────────────────────────────────────── */

export function initials(name: string | null | undefined): string {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/** `PENDING_APPROVAL` / `pending-approval` → `Pending approval`. */
export function humanize(value: string | null | undefined, fallback = '—'): string {
  if (!value) return fallback;
  const spaced = value.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function truncate(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max - 1)}…`;
}

/** `(555) 123-4567` when it looks like a US number, else the raw string. */
export function phone(value: string | null | undefined, fallback = '—'): string {
  if (!value) return fallback;
  const digits = value.replace(/\D/g, '');
  if (digits.length === 10)
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits.startsWith('1'))
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return value;
}

export function pluralize(count: number, singular: string, plural?: string): string {
  return count === 1 ? singular : (plural ?? `${singular}s`);
}

/** `3 items` — count and noun together, the usual list-footer phrasing. */
export function countLabel(count: number, singular: string, plural?: string): string {
  return `${num(count)} ${pluralize(count, singular, plural)}`;
}
